import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useBlockingDownload } from "../hooks/useBlockingDownload";
import {
  getExpenseCaptures, getLedgerSummary, downloadExpenseCapturesExport, downloadExpenseBundle, getBranches,
} from "../api";

const NAVY = "#0f172a";

const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};

const TAX_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "vat_claimable", label: "VAT claimable" },
  { value: "wht_pending", label: "WHT pending" },
  { value: "no_tax_invoice", label: "No tax invoice" },
];

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// "YYYY-MM" -> "YYYY-MM-DD" for the real last day of that month — `${month}-31` is not a
// valid calendar date for Feb/Apr/Jun/Sep/Nov and the backend (a plain `date` field) 422s
// on it, so this can't just be hardcoded.
function monthLastDay(month: string): string {
  const [year, mon] = month.split("-").map(Number);
  const lastDay = new Date(year, mon, 0).getDate(); // day 0 of next month = last day of this one
  return `${month}-${String(lastDay).padStart(2, "0")}`;
}

export default function ExpenseLedgerTab({ branchesEnabled }: { branchesEnabled: boolean }) {
  const [month, setMonth] = useState(currentMonth());
  const [branchId, setBranchId] = useState<number | "">("");
  const [taxStatus, setTaxStatus] = useState("");
  const { downloading, download } = useBlockingDownload();

  const { data: branchesResp } = useQuery({ queryKey: ["branches"], queryFn: getBranches, enabled: branchesEnabled });
  const branches = (branchesResp?.branches ?? []).filter(b => b.status === "active");

  const { data: summary } = useQuery({
    queryKey: ["ledger-summary", month, branchId],
    queryFn: () => getLedgerSummary({ month, branch_id: branchId || undefined }),
  });

  const { data: listResp, isLoading } = useQuery({
    queryKey: ["ledger-captures", month, branchId, taxStatus],
    queryFn: () => getExpenseCaptures({
      status: "confirmed",
      date_from: `${month}-01`,
      date_to: monthLastDay(month),
      branch_id: branchId || undefined,
      tax_status: taxStatus || undefined,
    }),
  });
  const captures: any[] = Array.isArray(listResp) ? listResp : (listResp?.items ?? []);

  async function handleExportCsv() {
    await download(() => downloadExpenseCapturesExport({
      status: "confirmed", date_from: `${month}-01`, date_to: monthLastDay(month),
      branch_id: branchId || undefined, tax_status: taxStatus || undefined,
    }), "Failed to export CSV");
  }

  async function handleSendToAccountant() {
    await download(
      () => downloadExpenseBundle({ month, branch_id: branchId || undefined }),
      "Failed to build the accountant bundle",
    );
  }

  return (
    <div>
      <LoadingOverlay active={downloading} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: NAVY, fontSize: 18 }}>Expenses — {month}</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
            All confirmed, tax-tagged{branchesEnabled ? ", branch-tagged" : ""}, ready for your accountant.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13 }} />
          <AsyncButton onClick={handleExportCsv} style={outlineBtnStyle}>Export CSV</AsyncButton>
          <AsyncButton onClick={handleSendToAccountant} style={{ ...outlineBtnStyle, color: "#1e3a8a", borderColor: "#93c5fd" }}>
            Send to accountant
          </AsyncButton>
        </div>
      </div>

      {/* Summary strip — each card can carry more than one currency line (C5-13): a
          VAT-reclaimable or WHT-owed total that silently added THB and INR amounts
          together would be meaningless, so these render one line per currency instead. */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ background: "#dcfce7", border: "1px solid #86efac", borderRadius: 10, padding: "10px 18px" }}>
          <div style={{ fontSize: 11, color: "#166534", fontWeight: 600 }}>VAT reclaimable</div>
          {(summary?.vat_reclaimable_by_currency ?? []).length === 0 ? (
            <div style={{ fontSize: 18, fontWeight: 700, color: "#166534" }}>฿0.00</div>
          ) : (summary!.vat_reclaimable_by_currency.map(c => (
            <div key={c.currency} style={{ fontSize: 18, fontWeight: 700, color: "#166534" }}>
              {c.currency_symbol}{c.total.toLocaleString("en", { minimumFractionDigits: 2 })}
            </div>
          )))}
          <div style={{ fontSize: 11, color: "#166534" }}>{summary?.vat_reclaimable_count ?? 0} full tax invoices</div>
        </div>
        <div style={{ background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "10px 18px" }}>
          {/* "WHT to issue" / "certificates owed" implied this product issues the
              หนังสือรับรองหักภาษี ณ ที่จ่าย. It does not, and deliberately will not — certificate
              issuance is the accountant's job (PO ruling 2026-07-31, capture-only). The card
              reports what was withheld on the documents captured; what to do about it is not
              this product's call to state. */}
          <div style={{ fontSize: 11, color: "#854d0e", fontWeight: 600 }}>WHT withheld</div>
          {(summary?.wht_owed_by_currency ?? []).length === 0 ? (
            <div style={{ fontSize: 18, fontWeight: 700, color: "#854d0e" }}>฿0.00</div>
          ) : (summary!.wht_owed_by_currency.map(c => (
            <div key={c.currency} style={{ fontSize: 18, fontWeight: 700, color: "#854d0e" }}>
              {c.currency_symbol}{c.total.toLocaleString("en", { minimumFractionDigits: 2 })}
            </div>
          )))}
          <div style={{ fontSize: 11, color: "#854d0e" }}>
            on {summary?.wht_owed_count ?? 0} expense{summary?.wht_owed_count !== 1 ? "s" : ""} — for your accountant
          </div>
        </div>
      </div>

      {/* Filters */}
      {branchesEnabled && branches.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          {[{ id: "", name: "All locations" }, ...branches].map(b => (
            <button key={b.id || "all"} onClick={() => setBranchId(b.id || "")} style={{
              padding: "5px 12px", borderRadius: 20, border: "1px solid #e2e8f0", cursor: "pointer", fontSize: 12,
              background: branchId === b.id || (b.id === "" && branchId === "") ? NAVY : "white",
              color: branchId === b.id || (b.id === "" && branchId === "") ? "white" : "#64748b",
            }}>{b.name}</button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {TAX_FILTERS.map(f => (
          <button key={f.value || "all"} onClick={() => setTaxStatus(f.value)} style={{
            padding: "5px 12px", borderRadius: 20, border: "1px solid #e2e8f0", cursor: "pointer", fontSize: 12,
            background: taxStatus === f.value ? "#1e3a8a" : "white",
            color: taxStatus === f.value ? "white" : "#64748b",
          }}>{f.label}</button>
        ))}
      </div>

      {isLoading && <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Loading…</div>}
      {!isLoading && (
        <div style={{ display: "grid", gap: 8 }}>
          {captures.map(e => (
            <div key={e.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>{e.vendor_name}</div>
                <div style={{ display: "flex", gap: 8, fontSize: 11, color: "#64748b", flexWrap: "wrap", marginTop: 2 }}>
                  <span style={{ background: "#f1f5f9", padding: "2px 8px", borderRadius: 20 }}>{e.category}</span>
                  {e.branch_name && <span style={{ background: "#eff6ff", color: "#1d4ed8", padding: "2px 8px", borderRadius: 20 }}>📍 {e.branch_name}</span>}
                  {e.doc_class === "full_tax_invoice" && <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 20 }}>VAT ✓</span>}
                  {e.wht_applicable && <span style={{ background: "#fef9c3", color: "#854d0e", padding: "2px 8px", borderRadius: 20 }}>WHT {e.wht_rate ?? ""}%</span>}
                  {e.source === "manual" && <span style={{ background: "#f1f5f9", padding: "2px 8px", borderRadius: 20 }}>Manual</span>}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: NAVY }}>
                  {e.currency || "THB"} {(e.total || 0).toLocaleString("en", { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>{e.expense_date}</div>
              </div>
            </div>
          ))}
          {captures.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
              No confirmed expenses match these filters for {month}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
